import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function CTA() {
  const { ref: sectionRef, isVisible } = useScrollReveal();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5j6cmagbpr25eot44rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setShowSuccess(true);
        form.reset();
        setTimeout(() => {
          setShowQuoteForm(false);
          setShowSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="py-12 sm:py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse animation-delay-400"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className={`text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          Ready to Improve Your Property's Energy Efficiency?
        </h2>
        <p className={`text-base sm:text-xl text-gray-300 mb-6 sm:mb-10 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          Get a free consultation and quote today
        </p>

        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a href="tel:9284278140" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 inline-flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer shadow-xl hover:shadow-2xl hover:scale-105 hover:-translate-y-1 transform group">
            <i className="ri-phone-line text-lg sm:text-xl group-hover:rotate-12 transition-transform duration-300"></i>
            <span>Call (928) 427-8140</span>
          </a>
          <button 
            onClick={() => setShowQuoteForm(true)}
            className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-blue-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer shadow-xl hover:shadow-2xl hover:scale-105 hover:-translate-y-1 transform">
            Request Free Quote
          </button>
        </div>

        {/* Trust Text */}
        <p className={`text-xs sm:text-sm text-gray-400 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          No obligation • Fast Response • Licensed & Insured
        </p>
      </div>

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4 animate-fade-in backdrop-blur-sm">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4 sm:p-6 rounded-t-xl flex justify-between items-center">
              <div className="animate-slide-in-left">
                <h3 className="text-lg sm:text-2xl font-bold">Request Your Free Quote</h3>
                <p className="text-blue-100 mt-1 text-xs sm:text-sm">We'll get back to you within 24 hours</p>
              </div>
              <button
                onClick={() => setShowQuoteForm(false)}
                className="text-white hover:bg-white/20 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center transition-all cursor-pointer hover:rotate-90 duration-300 animate-slide-in-right flex-shrink-0"
              >
                <i className="ri-close-line text-xl sm:text-2xl"></i>
              </button>
            </div>

            <form id="homepage_cta_quote" data-readdy-form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {showSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 sm:px-4 sm:py-3 rounded-lg flex items-center space-x-2 animate-slide-in-down text-xs sm:text-sm">
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl animate-bounce"></i>
                  <span>Success! Your quote request has been received. We'll contact you within 24 hours.</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="group animate-fade-in-up animation-delay-100">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-900">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-xs sm:text-sm transition-all duration-300 hover:border-blue-400"
                    placeholder="John Smith"
                  />
                </div>

                <div className="group animate-fade-in-up animation-delay-200">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-900">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-xs sm:text-sm transition-all duration-300 hover:border-blue-400"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="group animate-fade-in-up animation-delay-300">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-900">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-xs sm:text-sm transition-all duration-300 hover:border-blue-400"
                    placeholder="(928) 427-8140"
                  />
                </div>

                <div className="group animate-fade-in-up animation-delay-400">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-900">
                    Property Type *
                  </label>
                  <div className="relative">
                    <select
                      name="property_type"
                      required
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 pr-8 sm:pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-xs sm:text-sm cursor-pointer transition-all duration-300 appearance-none bg-white hover:border-blue-400"
                    >
                      <option value="">Select type</option>
                      <option value="Residential">Residential</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Industrial">Industrial</option>
                    </select>
                    <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-500 text-lg sm:text-xl transition-transform duration-300 group-focus-within:text-blue-900 group-focus-within:rotate-180"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="group animate-fade-in-up animation-delay-500">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-900">
                  Service Needed *
                </label>
                <div className="relative">
                  <select
                    name="service"
                    required
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 pr-8 sm:pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-xs sm:text-sm cursor-pointer transition-all duration-300 appearance-none bg-white hover:border-blue-400"
                  >
                    <option value="">Select service</option>
                    <option value="Spray Foam Insulation">Spray Foam Insulation</option>
                    <option value="Attic Insulation">Attic Insulation</option>
                    <option value="Wall Insulation">Wall Insulation</option>
                    <option value="Crawl Space Insulation">Crawl Space Insulation</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-500 text-lg sm:text-xl transition-transform duration-300 group-focus-within:text-blue-900 group-focus-within:rotate-180"></i>
                  </div>
                </div>
              </div>

              <div className="group animate-fade-in-up animation-delay-600">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-900">
                  Project Details
                </label>
                <textarea
                  name="project_details"
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-xs sm:text-sm resize-none transition-all duration-300 hover:border-blue-400"
                  placeholder="Tell us about your project, property size, timeline, etc."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <div className="group animate-fade-in-up animation-delay-700">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-900">
                  Preferred Contact Method
                </label>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <label className="flex items-center space-x-2 cursor-pointer hover:scale-105 transition-transform duration-200">
                    <input type="radio" name="contact_method" value="Phone" className="cursor-pointer accent-blue-900" />
                    <span className="text-xs sm:text-sm text-gray-700">Phone</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer hover:scale-105 transition-transform duration-200">
                    <input type="radio" name="contact_method" value="Email" className="cursor-pointer accent-blue-900" />
                    <span className="text-xs sm:text-sm text-gray-700">Email</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer hover:scale-105 transition-transform duration-200">
                    <input type="radio" name="contact_method" value="Text" className="cursor-pointer accent-blue-900" />
                    <span className="text-xs sm:text-sm text-gray-700">Text</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 sm:py-4 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer hover:scale-105 hover:shadow-xl duration-300 transform animate-fade-in-up animation-delay-800 text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </span>
                ) : (
                  'Submit Quote Request'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
